/**
 * 사용자 목록 테이블 컬럼 정의
 * 사용자 목록을 표시하기 위한 테이블 컬럼 설정을 정의합니다.
 */

// 이용자 목록 테이블 컬럼 정의
import moment from "moment";
import { BaseTooltip } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import DetailLink from "src/components/DetailLink";
import ProductTypeChangeLabel from "src/components/ProductTypeChangeLabel";
import PagePath from "src/pagePath.json";
import ContractManageIdLinkCell from "src/components/contract/ContractManageIdLinkCell";
import { getInvitationChannelLabel } from "src/pages/user/constants";
import { ProductTypeLabels } from "src/pages/product/product-types";
import { formatPhoneNumber, ViewYmdFormat } from "src/utils";

// ===== 공통 포맷터 (Cell / excelValue 양쪽에서 재사용) =====
const getMemberStatusLabel = (status?: string) => {
  return (
    (status === "MEMBER_INVITE" && "초대") ||
    (status === "MEMBER_USE" && "사용") ||
    (status === "MEMBER_DELETE" && "삭제") ||
    (status === "MEMBER_CLOSE" && "사용종료") ||
    (status === "MEMBER_EXPIRE" && "초대만료") ||
    "-"
  );
};

/**
 * 일시 컬럼 공통 포맷터
 * 공통규칙(ISO 8601)에 따라 요일 없이 날짜+시간을 노출한다 (2026-07-31 14:36:30)
 */
const formatDateTime = (value?: string) => {
  return value ? moment(value).format(ViewYmdFormat.FULL) : "-";
};

const getPhoneNumberText = (row: any) => {
  const phoneNumber = row?.phoneNumber ? row.phoneNumber : row?.inviteMobileNumber ? row.inviteMobileNumber : "-";
  if (phoneNumber === "-") return "-";
  return formatPhoneNumber(phoneNumber);
};

const getEmailText = (row: any) => {
  return row?.email ? row.email : row?.inviteEmail ? row.inviteEmail : "-";
};

const getProductTypeLabel = (type?: string) => {
  if (!type) return "-";
  return ProductTypeLabels[type as keyof typeof ProductTypeLabels] || "-";
};

const getWatchingCalendarLabel = (flag: boolean | undefined) => {
  if (flag === undefined) return "-";
  return flag === true ? "연동" : "미연동";
};

const getVerifiedLabel = (flag: boolean | undefined) => {
  if (flag === undefined) return "-";
  return String(flag);
};

const getRoleLabel = (isContractor?: boolean, isAdmin?: boolean) => {
  let roles = "";
  if (isContractor === true) {
    roles = "계약자 ";
  }
  if (isAdmin === true) {
    if (roles !== "") {
      roles += ", 마스터";
    } else {
      roles += "마스터";
    }
  }
  return roles || "-";
};

/**
 * 사용자 목록 테이블 컬럼 정의
 * 각 컬럼의 헤더, 접근자, 너비, 셀 렌더링 등을 설정합니다.
 *
 * 컬럼 순서는 [컬럼 개선] Ctrl.Room 이용자 기획의 그룹 정의를 따른다
 * 식별/상태 > 회원 정보 > 이용 관계 > 초대/이력 > 부가 설정
 * align: "center" 지정 컬럼은 BaseTable 에서 헤더/셀이 함께 가운데 정렬된다
 */
export const columns = [
  // ===== 식별/상태 =====
  {
    Header: "이용자 ID",
    accessor: "contractMemberId",
    sticky: "left",
    width: 120,
    Cell: ({ row, value }: any) => {
      const path = PagePath.user.detail.replace(":memberId", value) + "?contractApplyNumber=" + row.original.contractApplyNumber + "&tab=basic";
      return (
        <>
          <DetailLink to={path} className="text-underline text-center">
            {value}
          </DetailLink>
        </>
      );
    },
  },

  {
    Header: "상태",
    accessor: "memberStatus",
    sticky: "left",
    width: 80,
    align: "center",
    Cell: ({ value }: any) => {
      return (
        //visible 녹색
        //invisible 빨간색
        //draft = 회색
        <div className="flex-center">
          <p
            className={`${(value === "MEMBER_INVITE" && "status A") ||
              (value === "MEMBER_USE" && "status B") ||
              (value === "MEMBER_DELETE" && "status D") ||
              (value === "MEMBER_CLOSE" && "status D") ||
              (value === "MEMBER_EXPIRE" && "status D")
              }`}
          >
            {getMemberStatusLabel(value)}
          </p>
        </div>
      );
    },
    excelValue: (row: any) => getMemberStatusLabel(row.memberStatus),
  },

  // ===== 회원 정보 =====
  {
    Header: "회원번호",
    accessor: "memberNo",
    width: 120,
    sticky: "left",
    Cell: ({ value }: any) => {
      return (
        <>
          {value ? (
            <BaseNewTabLink path={PagePath.user.list + `?size=20&page=0&searchType=MEMBER_NO&searchValue=${value}`} value={value}></BaseNewTabLink>
          ) : (
            "-"
          )}
        </>
      );
    },
  },
  {
    Header: "휴대폰번호",
    accessor: "phoneNumber",
    width: 160,
    Cell: ({ row }: any) => {
      return (
        <div className="flex-center-start w-100">
          {getPhoneNumberText(row.original)}
          {row.original?.isPhoneNumberVerified && <div className="ml4 ic_certificate shrink-0"></div>}
        </div>
      );
    },
    excelValue: (row: any) => getPhoneNumberText(row),
  },
  {
    Header: "이메일",
    accessor: "email",
    width: 160,
    Cell: ({ row }: any) => {
      return (
        <div className="flex-center-start w-100">
          <BaseTooltip isSingleLine contents={getEmailText(row.original)}></BaseTooltip>

          {row.original?.isEmailVerified && <div className="ml4 ic_certificate shrink-0"></div>}
        </div>
      );
    },
    excelValue: (row: any) => getEmailText(row),
  },
  {
    Header: "닉네임",
    accessor: "nickname",
    width: 140,
    Cell: ({ value }: any) => {
      return <BaseTooltip className="flex-center-start w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: any) => row.nickname || "-",
  },
  {
    // 기획서 컬럼 순서표에는 없는 컬럼이라 회원 정보 그룹 마지막에 배치했다 (MVDV-9305 로 추가된 컬럼)
    Header: "닉네임(추가)",
    accessor: "secondaryNickname",
    width: 140,
    Cell: ({ value }: any) => {
      return <BaseTooltip className="flex-center-start w-100" contents={value || "-"}></BaseTooltip>;
    },
    excelValue: (row: any) => row.secondaryNickname || "-",
  },

  // ===== 이용 관계 =====
  {
    // 신청번호(난수) 대체 컬럼
    Header: "계약관리 ID",
    accessor: "contractManageId",
    width: 120,
    Cell: ({ data, row }: any) => (
      <ContractManageIdLinkCell
        contractManageId={row.original.contractManageId}
        contractManageIdsInPage={data.map((item: any) => item.contractManageId)}
      />
    ),
    excelValue: (row: any) => (row.contractManageId ? String(row.contractManageId) : "-"),
  },
  {
    Header: "계약명",
    accessor: "contractName",
    width: 160,
    Cell: ({ value }: any) => {
      return <BaseTooltip className="flex-center-start w-100" contents={value || "-"}></BaseTooltip>;
    },
    // 목록은 말줄임으로 노출하되 엑셀은 값 전체를 내려준다
    excelValue: (row: any) => row.contractName || "-",
  },
  {
    // 기획서 TO-BE 표 기준으로 계약명 우측에 배치
    Header: "권한",
    accessor: "role",
    width: 120,
    Cell: ({ row }: any) => {
      const roles = getRoleLabel(row.original.isContractor, row.original.isAdmin);
      return <div>{roles}</div>;
    },
    excelValue: (row: any) => getRoleLabel(row.isContractor, row.isAdmin),
  },
  {
    Header: "상품 ID",
    accessor: "spaceProductId",
    width: 120,
    Cell: ({ row, value }: any) => {
      const detailPath = `${PagePath.product.detail.replace(":id", `${value}`)}`;
      return <>{value ? <BaseNewTabLink path={detailPath} value={value}></BaseNewTabLink> : "-"}</>;
    },
  },
  {
    Header: "상품명",
    accessor: "spaceProductName",
    width: 240,
    Cell: ({ value }: any) => {
      return <BaseTooltip className="flex-center-start w-100" contents={value || "-"}></BaseTooltip>;
    },
  },
  {
    Header: "상품유형",
    accessor: "spaceProductType",
    width: 80,
    align: "center",
    Cell: ({ value }: any) => {
      return <ProductTypeChangeLabel productType={value} />;
    },
    excelValue: (row: any) => getProductTypeLabel(row.spaceProductType),
  },

  // ===== 초대/이력 =====
  {
    Header: "초대일시",
    accessor: "inviteDate",
    width: 160,
    align: "center",
    Cell: ({ value }: any) => {
      return <span>{formatDateTime(value)}</span>;
    },
    excelValue: (row: any) => formatDateTime(row.inviteDate),
  },
  {
    Header: "초대만료일시",
    accessor: "inviteExpireDate",
    width: 160,
    align: "center",
    Cell: ({ value }: any) => {
      return <span>{formatDateTime(value)}</span>;
    },
    excelValue: (row: any) => formatDateTime(row.inviteExpireDate),
  },
  {
    Header: "인증일시",
    accessor: "joinedDate",
    width: 160,
    align: "center",
    Cell: ({ value }: any) => {
      return <span>{formatDateTime(value)}</span>;
    },
    excelValue: (row: any) => formatDateTime(row.joinedDate),
  },
  {
    Header: "상태변경일시",
    accessor: "statusDate",
    width: 160,
    align: "center",
    Cell: ({ value }: any) => {
      return <span>{formatDateTime(value)}</span>;
    },
    excelValue: (row: any) => formatDateTime(row.statusDate),
  },
  {
    Header: "초대자 이메일",
    accessor: "inviteeMemberId",
    width: 160,
    Cell: ({ value }: any) => {
      return <BaseTooltip className="flex-center-start w-100" isSingleLine contents={value || "-"} />;
    },
  },
  {
    Header: "초대자 회원번호",
    accessor: "inviteeMemberNo",
    width: 120,
    Cell: ({ value }: any) => {
      return <BaseTooltip contents={value || "-"} />;
    },
  },
  {
    Header: "초대경로",
    accessor: "invitationChannel",
    width: 120,
    align: "center",
    Cell: ({ value }: any) => {
      return <span>{getInvitationChannelLabel(value)}</span>;
    },
    excelValue: (row: any) => getInvitationChannelLabel(row.invitationChannel),
  },

  // ===== 부가 설정 =====
  {
    Header: "캘린더 연동 여부",
    accessor: "isWatchingCalendar",
    width: 160,
    align: "center",
    Cell: ({ row }: any) => {
      if (row.original.isWatchingCalendar === undefined) return <div>-</div>;

      return <div>{getWatchingCalendarLabel(row.original.isWatchingCalendar)}</div>;
    },
    excelValue: (row: any) => getWatchingCalendarLabel(row.isWatchingCalendar),
  },
];

const phoneNumberVerifiedColumn = {
  Header: "휴대폰번호 인증여부",
  accessor: "isPhoneNumberVerified",
  excelValue: (row: any) => getVerifiedLabel(row.isPhoneNumberVerified),
};

const emailVerifiedColumn = {
  Header: "이메일 인증여부",
  accessor: "isEmailVerified",
  excelValue: (row: any) => getVerifiedLabel(row.isEmailVerified),
};

export const excelColumns = columns.flatMap((column) => {
  if (column.accessor === "phoneNumber") return [column, phoneNumberVerifiedColumn];
  if (column.accessor === "email") return [column, emailVerifiedColumn];
  return [column];
});
